import FetchClient, { type ApiResponse } from "./FetchClient";
import type { RetailerType } from "../config/retailers";

export interface StoreProductResult {
  storeId: string;
  storeAddress: string;
  name: string;
  barcode: string;
  storePrice: number;
  onlinePrice: number;
  msrp: number;
  image: string;
  mainCategory: string;
  categoryPath: string;
  categoryPathId: string;
  brand: string;
  inventory: number;
  possiblePenny?: boolean;
  verificationStatus?: string;
  verificationId?: string;
  inventoryExact?: boolean;
  clearance?: boolean;
  backroom: number;
  status: string;
  aisles: string;
  sku: string;
  distance?: string;
  error?: string;
}

export interface ProductSearchResponse {
  results: StoreProductResult[];
  scope?: string;
  totalStores?: number;
  inStockCount?: number;
  searchDurationMs?: number;
  // Amazon pricing (response-level)
  amazonPrice?: number;
  amazonTitle?: string;
  amazonURL?: string;
  amazonASIN?: string;
  amazonRating?: number;
  amazonReviews?: number;
  amazonIsPrime?: boolean;
  amazonThumbnail?: string;
}

export interface ProductSearchRequest {
  code: string;
  searchType: "UPC" | "SKU";
  zipCode: string;
}

export interface QrCodeRequest {
  qrUrl: string;
}

export interface QrCodeResponse {
  sku: string;
}

// Home Depot specific interfaces
export interface HomeDepotStoreProductResult {
  storeId: string;
  storeAddress: string;
  name: string;
  itemId: string;
  storePrice: number;
  originalPrice?: number;
  clearancePrice?: number;
  clearance?: boolean;
  msrp: number;
  image: string;
  brand: string;
  inventory: number;
  possiblePenny?: boolean;
  verificationStatus?: string;
  verificationId?: string;
  backroom: number;
  status: string;
  sku: string;
  upc: string;
  barcode: string;
  modelNumber: string;
  aisles?: string;
  error?: string;
  inStock: boolean;
  availability: string;
  canonicalUrl: string;
}

export interface HomeDepotMultiStoreSearchResponse {
  results: HomeDepotStoreProductResult[];
  scope?: string;
  totalStores?: number;
  inStockCount?: number;
  searchDurationMs?: number;
  amazonPrice?: number;
  amazonTitle?: string;
  amazonURL?: string;
  amazonASIN?: string;
  amazonRating?: number;
  amazonReviews?: number;
  amazonIsPrime?: boolean;
  amazonThumbnail?: string;
}

export interface HomeDepotMultiStoreSearchRequest {
  itemId: string;
  zipCode: string;
  storeId?: string;
  scope?: "local" | "nationwide";
}

export type HomeDepotSearchScope = "local" | "nationwide";

interface RequestOptions {
  signal?: AbortSignal;
  provider?: string;
  bypassCache?: boolean;
  scope?: HomeDepotSearchScope;
  storeId?: string;
}

const homeDepotSearchErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.name === "AbortError") {
    return "Home Depot search was canceled.";
  }

  const status = (error as { status?: unknown } | null)?.status;
  if (
    (typeof status === "number" && status >= 500) ||
    error instanceof TypeError
  ) {
    return "Home Depot search is temporarily unavailable. Please try again shortly.";
  }

  return error instanceof Error && error.message.trim()
    ? error.message
    : "Failed to search for Home Depot product";
};

export const searchProduct = async (
  code: string,
  searchType: "UPC" | "SKU" = "UPC",
  zipCode: string,
  retailer: RetailerType = "walmart",
  options: RequestOptions = {}
): Promise<ProductSearchResponse> => {
  try {
    // Woot doesn't support product search
    if (retailer === "woot") {
      throw new Error("Product search is not available for Woot");
    }

    // For Home Depot, use the multi-store search endpoint
    if (retailer === "home_depot") {
      return await searchHomeDepotMultiStore(code, zipCode, options);
    }

    // For Dollar General, use the Dollar General product search endpoint
    if (retailer === "dollar_general") {
      return await searchDollarGeneralProduct(code, zipCode, options);
    }

    // For Target, use the Target product search endpoint
    if (retailer === "target") {
      return await searchTargetProduct(code, zipCode, options);
    }

    // For Best Buy, use the Best Buy product search endpoint
    if (retailer === "bestbuy") {
      return await searchBestBuyProduct(code, zipCode, options);
    }

    // For Costco, use the Costco product search endpoint
    if (retailer === "costco") {
      return await searchCostcoProduct(code, zipCode, options);
    }

    if (retailer === "tractor_supply") {
      return await searchTractorSupplyProduct(code, zipCode, options);
    }

    if (retailer === "northern_tool") {
      throw new Error(
        "Northern Tool product search is not available yet. Browse your selected store's clearance deals instead."
      );
    }

    if (retailer === "samsclub") {
      return await searchSamsClubProduct(code, zipCode, options);
    }

    // For Lowe's, use nearby store lookup from product card context
    if (retailer === "lowes") {
      return await searchLowesProduct(code, zipCode, options);
    }

    // For Walmart, use the existing product search endpoint
    const requestBody: Record<string, string | boolean> = {
      upc: code,
      type: searchType,
      zipCode: zipCode,
    };
    if (options.provider) {
      requestBody.provider = options.provider;
    }
    if (options.bypassCache) {
      requestBody.bypassCache = true;
    }

    const response = await FetchClient.post<ApiResponse<ProductSearchResponse>>(
      "/product-search",
      requestBody,
      { signal: options.signal }
    );

    // Check if response is a string (plain text error from backend)
    if (typeof response === "string") {
      throw new Error(response);
    }

    if (response.data) {
      return response.data;
    }

    throw new Error("No product found. Please verify the code and try again.");
  } catch (error: unknown) {
    // Extract the error message directly from the error
    const message =
      error instanceof Error ? error.message : "Failed to search for product";
    
    console.error("Product search error:", message, error);

    // Re-throw with the original message preserved
    throw new Error(message);
  }
};

export const processQrCode = async (qrUrl: string, options: RequestOptions = {}): Promise<string> => {
  try {
    const response = await FetchClient.post<ApiResponse<QrCodeResponse>>(
      "/qr-redirect",
      { qrUrl },
      { signal: options.signal }
    );

    // Check if response is a string (plain text error from backend)
    if (typeof response === "string") {
      throw new Error(response);
    }

    if (response.data?.sku) {
      return response.data.sku;
    }

    throw new Error("No SKU received from QR code");
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : "Failed to process QR code";
    console.error("QR code processing error:", message, error);
    throw new Error(message);
  }
};

export const searchHomeDepotProduct = async (
  itemId: string,
  zipCode: string,
  options: RequestOptions = {}
): Promise<HomeDepotMultiStoreSearchResponse> => {
  try {
    const requestBody: HomeDepotMultiStoreSearchRequest = {
      itemId: itemId,
      zipCode: zipCode,
      ...(options.storeId ? { storeId: options.storeId } : {}),
      ...(options.scope ? { scope: options.scope } : {}),
    };

    const response = await FetchClient.post<
      ApiResponse<HomeDepotMultiStoreSearchResponse>
    >("/home-depot-product-search", requestBody, { signal: options.signal });

    // Check if response is a string (plain text error from backend)
    if (typeof response === "string") {
      throw new Error(response);
    }

    if (response.data) {
      return response.data;
    }

    throw new Error("No product found. Please verify the item ID and try again.");
  } catch (error: unknown) {
    const message = homeDepotSearchErrorMessage(error);
    console.error("Home Depot product search error:", message, error);
    throw new Error(message);
  }
};

// Helper function to convert Home Depot results to StoreProductResult format
const convertHomeDepotToStoreResults = (
  homeDepotResults: HomeDepotStoreProductResult[]
): StoreProductResult[] => {
  return homeDepotResults.map((result) => ({
    storeId: result.storeId,
    storeAddress: result.storeAddress,
    name: result.name,
    barcode: result.barcode || result.upc || result.sku,
    storePrice: result.storePrice,
    onlinePrice: result.originalPrice || result.storePrice,
    msrp: result.msrp || result.originalPrice || result.storePrice,
    image: result.image,
    mainCategory: "", // Home Depot doesn't provide main category in this format
    categoryPath: "", // Home Depot doesn't provide category path in this format
    categoryPathId: "",
    brand: result.brand,
    inventory: result.inventory,
    possiblePenny: result.possiblePenny,
    verificationStatus: result.verificationStatus,
    verificationId: result.verificationId,
    clearance: result.clearance,
    backroom: result.backroom,
    status: result.status,
    aisles: result.aisles || "", // Use aisle if available
    sku: result.itemId || result.sku,
    error: result.error,
  }));
};

// Search Home Depot multi-store and convert to standard format
const searchHomeDepotMultiStore = async (
  itemId: string,
  zipCode: string,
  options: RequestOptions = {}
): Promise<ProductSearchResponse> => {
  try {
    const homeDepotResponse = await searchHomeDepotProduct(itemId, zipCode, options);
    const convertedResults = convertHomeDepotToStoreResults(
      homeDepotResponse.results
    );

    return {
      results: convertedResults,
      scope: homeDepotResponse.scope,
      totalStores: homeDepotResponse.totalStores,
      inStockCount: homeDepotResponse.inStockCount,
      searchDurationMs: homeDepotResponse.searchDurationMs,
      amazonPrice: homeDepotResponse.amazonPrice,
      amazonTitle: homeDepotResponse.amazonTitle,
      amazonURL: homeDepotResponse.amazonURL,
      amazonASIN: homeDepotResponse.amazonASIN,
      amazonRating: homeDepotResponse.amazonRating,
      amazonReviews: homeDepotResponse.amazonReviews,
      amazonIsPrime: homeDepotResponse.amazonIsPrime,
      amazonThumbnail: homeDepotResponse.amazonThumbnail,
    };
  } catch (error: unknown) {
    console.error("Home Depot multi-store search error:", error);
    throw error;
  }
};

// Dollar General product search
export const searchDollarGeneralProduct = async (
  upc: string,
  zipCode: string,
  options: RequestOptions = {}
): Promise<ProductSearchResponse> => {
  try {
    const requestBody = {
      upc: upc,
      zipCode: zipCode,
    };

    const response = await FetchClient.post<ApiResponse<ProductSearchResponse>>(
      "/dollar-general-product-search",
      requestBody,
      { signal: options.signal }
    );

    // Check if response is a string (plain text error from backend)
    if (typeof response === "string") {
      throw new Error(response);
    }

    if (response.data) {
      return response.data;
    }

    throw new Error("No product found. Please verify the UPC and try again.");
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : "Failed to search for Dollar General product";
    console.error("Dollar General product search error:", message, error);
    throw new Error(message);
  }
};

// Target product search
export const searchTargetProduct = async (
  tcin: string,
  zipCode: string,
  options: RequestOptions = {}
): Promise<ProductSearchResponse> => {
  try {
    const requestBody = {
      tcin: tcin,
      zipCode: zipCode,
    };

    const response = await FetchClient.post<ApiResponse<ProductSearchResponse>>(
      "/target-product-search",
      requestBody,
      { signal: options.signal }
    );

    // Check if response is a string (plain text error from backend)
    if (typeof response === "string") {
      throw new Error(response);
    }

    if (response.data) {
      return response.data;
    }

    throw new Error("No product found. Please verify the TCIN and try again.");
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : "Failed to search for Target product";
    console.error("Target product search error:", message, error);
    throw new Error(message);
  }
};

// Best Buy product search
export const searchBestBuyProduct = async (
  sku: string,
  zipCode: string,
  options: RequestOptions = {}
): Promise<ProductSearchResponse> => {
  try {
    const requestBody = {
      sku: sku,
      zipCode: zipCode,
    };

    const response = await FetchClient.post<ApiResponse<ProductSearchResponse>>(
      "/bestbuy-product-search",
      requestBody,
      { signal: options.signal }
    );

    if (typeof response === "string") {
      throw new Error(response);
    }

    if (response.data) {
      return response.data;
    }

    throw new Error("No product found. Please verify the SKU and try again.");
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : "Failed to search for Best Buy product";
    console.error("Best Buy product search error:", message, error);
    throw new Error(message);
  }
};

export interface LowesNearbySearchRequest {
  omniItemId: string;
  itemNumber?: string;
  modelId?: string;
  vendorNumber?: string;
  zipCode: string;
  name?: string;
  brand?: string;
  barcode?: string;
  image?: string;
  url?: string;
  storePrice?: number;
  msrp?: number;
}

export const searchLowesNearby = async (
  request: LowesNearbySearchRequest,
  options: RequestOptions = {}
): Promise<ProductSearchResponse> => {
  try {
    const response = await FetchClient.post<ApiResponse<ProductSearchResponse>>(
      "/lowes-product-search",
      request,
      { signal: options.signal }
    );

    if (typeof response === "string") {
      throw new Error(response);
    }

    if (response.data) {
      return response.data;
    }

    throw new Error("No product found. Please verify the item and try again.");
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : "Failed to search nearby Lowe's stores";
    console.error("Lowe's nearby search error:", message, error);
    throw new Error(message);
  }
};

// Lowe's nearby search (legacy itemId-only payload)
export const searchLowesProduct = async (
  itemId: string,
  zipCode: string,
  options: RequestOptions = {}
): Promise<ProductSearchResponse> => {
  return searchLowesNearby({ omniItemId: itemId, itemNumber: itemId, zipCode }, options);
};

// ============================================
// Barcode photo decode (local ZXing first, AI server fallback)
// ============================================

const downscaleImage = (file: File, maxDim = 1280): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d")?.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Failed to process image"))),
        "image/jpeg",
        0.85
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });

const decodeBarcodePhotoLocally = async (file: File): Promise<string | null> => {
  const { BrowserMultiFormatReader, DecodeHintType } = await import("@zxing/library");
  // Photos are harder than live camera frames; spend extra effort before the AI fallback
  const hints = new Map([[DecodeHintType.TRY_HARDER, true]]);
  const reader = new BrowserMultiFormatReader(hints);
  const url = URL.createObjectURL(file);
  try {
    const result = await reader.decodeFromImageUrl(url);
    return result.getText() || null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
    reader.reset();
  }
};

const decodeBarcodePhotoRemotely = async (
  file: File,
  options: RequestOptions = {}
): Promise<string> => {
  const photo = await downscaleImage(file);
  const form = new FormData();
  form.append("photo", photo, "barcode.jpg");

  const response = await FetchClient.postForm<ApiResponse<{ upc: string }>>(
    "/barcode-decode",
    form,
    { signal: options.signal }
  );
  if (typeof response === "string") throw new Error(response);
  if (response.data?.upc) return response.data.upc;
  throw new Error("No barcode detected in the photo");
};

// Decodes a barcode from an uploaded photo. Tries ZXing locally (free, instant),
// then falls back to the server's AI vision endpoint for hard photos.
export const decodeBarcodePhoto = async (
  file: File,
  options: RequestOptions = {}
): Promise<string> => {
  const localResult = await decodeBarcodePhotoLocally(file);
  if (localResult) return localResult;

  try {
    return await decodeBarcodePhotoRemotely(file, options);
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : "Failed to read barcode from photo";
    console.error("Barcode photo decode error:", message, error);
    throw new Error(message);
  }
};

// Costco product search
export const searchCostcoProduct = async (
  sku: string,
  zipCode: string,
  options: RequestOptions = {}
): Promise<ProductSearchResponse> => {
  try {
    const response = await FetchClient.post<ApiResponse<ProductSearchResponse>>(
      "/costco-product-search",
      { sku, zipCode },
      { signal: options.signal }
    );

    if (typeof response === "string") {
      throw new Error(response);
    }

    if (response.data) {
      return response.data;
    }

    throw new Error("No product found. Please verify the SKU and try again.");
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : "Failed to search for Costco product";
    console.error("Costco product search error:", message, error);
    throw new Error(message);
  }
};

export const searchTractorSupplyProduct = async (
  code: string,
  zipCode: string,
  options: RequestOptions = {}
): Promise<ProductSearchResponse> => {
  try {
    const response = await FetchClient.post<ApiResponse<ProductSearchResponse>>(
      "/tractor-supply-product-search",
      { code, zipCode },
      { signal: options.signal }
    );
    if (typeof response === "string") throw new Error(response);
    if (response.data) return response.data;
    throw new Error("No product found. Please verify the item number, SKU, or UPC.");
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Failed to search Tractor Supply";
    console.error("Tractor Supply product search error:", message, error);
    throw new Error(message);
  }
};

// Sam's Club pins each club on its own proxied session, so a search takes
// 20-60 s. FetchClient sets no client timeout; the caller's AbortSignal (if any)
// still cancels it.
export const searchSamsClubProduct = async (
  code: string,
  zipCode: string,
  options: RequestOptions = {}
): Promise<ProductSearchResponse> => {
  try {
    const response = await FetchClient.post<ApiResponse<ProductSearchResponse>>(
      "/samsclub-product-search",
      { code, zipCode },
      { signal: options.signal }
    );
    if (typeof response === "string") throw new Error(response);
    if (response.data) return response.data;
    throw new Error("No product found. Please verify the UPC or Sam's Club item number.");
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Failed to search Sam's Club";
    console.error("Sam's Club product search error:", message, error);
    throw new Error(message);
  }
};
